import { useEffect, useRef, useState } from "react";
import type { PumpInfo } from "../types";

/* Polar → cartesian, and an SVG arc path between two angles (degrees). Used by
   the Pump schematic and the RadialGauge. */
export const POL = (cx: number, cy: number, deg: number, r: number): [number, number] => [
  cx + r * Math.cos((deg * Math.PI) / 180),
  cy + r * Math.sin((deg * Math.PI) / 180),
];
export const ARC = (cx: number, cy: number, s: number, e: number, r: number): string => {
  const [x1, y1] = POL(cx, cy, s, r);
  const [x2, y2] = POL(cx, cy, e, r);
  const large = Math.abs(e - s) > 180 ? 1 : 0;
  return `M${x1.toFixed(1)} ${y1.toFixed(1)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(1)} ${y2.toFixed(1)}`;
};

/** One sampled pump reading for the live pressure-cycle trace. */
export interface PumpReading {
  t: number;
  pressure: number;
  p1: number;
  p2: number;
  dp: number;
  flow: number;
  on: boolean;
}

/**
 * Accumulate the polled pump telemetry into a rolling trace for the live chart.
 * These are REAL sampled values straight from /api/state (the worker's persisted,
 * valve-driven pump model — or a real HA sensor once installed) — NOT a fabricated
 * client-side waveform. The trace fills in as the dashboard polls.
 */
export function usePumpHistory(pump: PumpInfo | undefined, cap = 260): PumpReading[] {
  const [hist, setHist] = useState<PumpReading[]>([]);
  const n = useRef(0);
  useEffect(() => {
    if (!pump) return;
    setHist((h) =>
      [...h, { t: n.current++, pressure: pump.pressure, p1: pump.p1, p2: pump.p2, dp: pump.dp, flow: pump.flow, on: pump.on }].slice(-cap),
    );
  }, [pump, cap]);
  return hist;
}

/**
 * A requestAnimationFrame clock (seconds) for COSMETIC motion only — the impeller
 * spin, the flowing-pipe dashes, the flow waveform. It carries no data; every
 * number on the page comes from `state.pump`.
 */
export function useAnimationTime(active = true): number {
  const t = useRef(0);
  const [, setFrame] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf = 0;
    let prev = performance.now();
    const loop = (now: number) => {
      t.current += Math.min(0.05, (now - prev) / 1000);
      prev = now;
      setFrame((f) => (f + 1) & 65535);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [active]);
  return t.current;
}
